import { ReactNode, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useAuthStore } from '../../store/authStore';
import {
  ArrowRightStartOnRectangleIcon,
  Bars3Icon,
  BuildingOfficeIcon,
  ChevronRightIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Box, Typography } from '@mui/material';
import Button from '../ui/Button';
import pkg from '../../../package.json';

interface AppShellProps {
  children: ReactNode;
}

export default function AppLayout({ children }: AppShellProps) {
  const { logout, user } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const NavItem = ({
    icon,
    label,
    to,
    active = false,
  }: {
    icon: ReactNode;
    label: string;
    to: string;
    active?: boolean;
  }) => {
    return (
      <li>
        <Button
          variant="text"
          size="md"
          leftIcon={icon}
          rightIcon={
            active ? (
              <ChevronRightIcon style={{ width: '16px', height: '16px' }} />
            ) : undefined
          }
          onClick={() => {
            navigate(to);
            setIsMobileMenuOpen(false);
          }}
          sx={{
            width: '100%',
            justifyContent: 'flex-start',
            mb: 0.5,
            color: active ? 'primary.main' : 'text.secondary',
            backgroundColor: active
              ? theme => theme.statusBackgrounds.primaryLight
              : 'transparent',
            '&:hover': {
              backgroundColor: active
                ? theme => theme.statusBackgrounds.primaryMedium
                : 'action.hover',
              color: active ? 'primary.main' : 'primary.main',
            },
          }}
        >
          {label}
        </Button>
      </li>
    );
  };

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        backgroundColor: 'background.default',
        overflow: 'hidden',
      }}
    >
      {/* Mobile menu trigger */}
      <Box
        sx={{
          display: { sm: 'none' },
          position: 'fixed',
          top: 16,
          left: 16,
          zIndex: 50,
        }}
      >
        <Box
          component="button"
          sx={{
            p: 1.5,
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: '50%',
            border: 'none',
            cursor: 'pointer',
            boxShadow: theme => theme.shadows[3],
            '&:hover': {
              backgroundColor: 'primary.dark',
            },
          }}
          onClick={() => setIsMobileMenuOpen(true)}
        >
          <Bars3Icon
            style={{
              width: '20px',
              height: '20px',
            }}
          />
        </Box>
      </Box>

      {/* Sidebar - desktop */}
      <Box
        component="aside"
        sx={{
          display: { xs: 'none', sm: 'flex' },
          flexDirection: 'column',
          width: '256px',
          backgroundColor: 'background.paper',
          borderRight: theme => `1px solid ${theme.palette.divider}`,
          height: '100vh',
          flexShrink: 0,
        }}
      >
        <Box
          component="nav"
          sx={{
            flex: 1,
            px: 2,
            py: 2.5,
            overflowY: 'auto',
          }}
        >
          <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
            <NavItem
              icon={<UsersIcon style={{ width: '20px', height: '20px' }} />}
              label="Leads"
              to="/leads"
              active={location.pathname === '/leads'}
            />
            <NavItem
              icon={
                <BuildingOfficeIcon style={{ width: '20px', height: '20px' }} />
              }
              label="Operators"
              to="/operators"
              active={location.pathname === '/operators'}
            />
          </Box>
        </Box>

        <Box
          sx={{
            borderTop: theme => `1px solid ${theme.palette.divider}`,
            p: 2,
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mb: 0, lineHeight: 1.2 }}
            >
              v{pkg.version}
              {user?.email && (
                <>
                  <br />
                  {user.email}
                </>
              )}
            </Typography>
          </Box>
          <Button
            variant="contained"
            leftIcon={
              <ArrowRightStartOnRectangleIcon
                style={{ width: '18px', height: '18px' }}
              />
            }
            onClick={handleLogout}
            isFullWidth
          >
            Logout
          </Button>
        </Box>
      </Box>

      {/* Mobile sidebar */}
      {isMobileMenuOpen && (
        <Box
          sx={theme => ({
            display: { sm: 'none' },
            position: 'fixed',
            inset: 0,
            backgroundColor: theme.palette.action.hover,
            zIndex: 40,
          })}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              bottom: 0,
              width: '256px',
              maxWidth: '80vw',
              backgroundColor: 'background.paper',
              boxShadow: theme => theme.shadows[3],
              zIndex: 30,
            }}
            onClick={e => e.stopPropagation()}
          >
            <Box
              component="nav"
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Mobile close button */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: theme => `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Box
                  component="button"
                  sx={{
                    color: 'text.secondary',
                    '&:hover': {
                      color: 'text.primary',
                    },
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <XMarkIcon
                    style={{
                      width: '20px',
                      height: '20px',
                    }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  overflowY: 'auto',
                }}
              >
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                  <NavItem
                    icon={
                      <UsersIcon style={{ width: '20px', height: '20px' }} />
                    }
                    label="Leads"
                    to="/leads"
                    active={location.pathname === '/leads'}
                  />
                  <NavItem
                    icon={
                      <BuildingOfficeIcon
                        style={{ width: '20px', height: '20px' }}
                      />
                    }
                    label="Operators"
                    to="/operators"
                    active={location.pathname === '/operators'}
                  />
                </Box>
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderTop: theme => `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 0,
                    marginBottom: '0 !important',
                    textAlign: 'center',
                  }}
                >
                  v{pkg.version}
                </Typography>
                {user?.email && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      mb: 1,
                      textAlign: 'center',
                    }}
                  >
                    {user.email}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  leftIcon={
                    <ArrowRightStartOnRectangleIcon
                      style={{ width: '18px', height: '18px' }}
                    />
                  }
                  onClick={handleLogout}
                  isFullWidth
                >
                  Logout
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flex: 1,
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
